import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Grid,
  TextField,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Button,
  Typography,
  FormControlLabel,
  Checkbox,
  useMediaQuery,
  useTheme,
  alpha,
  Tooltip,
  Chip,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import { useNavigate, useLocation } from "react-router-dom";
import {
  updatepos,
  getCategories,
  fetchItemsByCategoryAndSubcategory,
  GetApprovedSuppliers as fetchSuppliers,
  getUnits,
  ApproveSolistapi,
  postpos,
  GetNextPoNo,
  getSizes,
  getCompanyProfile,
  reserveMOSItemStock,
  releaseMOSItemStock,
} from "../../../Services/Services";
import Autocomplete from "@mui/material/Autocomplete";
import { toast, ToastContainer } from "react-toastify";
import dayjs from "dayjs";
import { debounce } from "lodash";

const createEmptyItem = () => ({
  line_id: null,
  size: null, // Product Type
  category: null,
  item: null,
  description: "",
  required_qty: 0,
  current_stock: 0,
  reserve_stock: 0,
  free_stock: 0,
  mos_reserved_qty: 0,
  reserve_input_qty: "",
  quantity: 0,
  used_qty: 0,
  reserve_qty: 0,
  unit: "",
  unit_name: "",
  rate: "",
  amount: "",
});

const getDefaultTerms = (division) => [
  { key: "Delivery Period", value: "Ready stock", is_default: true },
  { key: "Warranty", value: "12 months from delivery", is_default: true },
  { key: "Freight", value: "Ex works. Raipur", is_default: true },
  { key: "Inspection", value: "By buyer at site", is_default: true },
  { key: "Quality", value: "As per specifications", is_default: true },
  { key: "Rejection/Claim", value: "Allowed within 7 days", is_default: true },
  { key: "Jurisdiction", value: "Raipur", is_default: true },
  { key: "Payment Terms", value: "Material received after 30 days", is_default: true },
  { key: "GST", value: "18% applicable", is_default: true },
  { key: "Billing gst no-", value: division?.gst_no || "", is_default: true },
];

export default function PurchaseOrderForm() {
  const stored = localStorage.getItem("selectedDivision");
  const division = stored ? JSON.parse(stored) : null;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef();

  const { poData, isEdit } = location.state || {};

  const [companyProfile, setCompanyProfile] = useState(null);

  const [suppliers, setSuppliers] = useState([]);
  const [supplierLoading, setSupplierLoading] = useState(false);
  const [supplierSearch, setSupplierSearch] = useState("");

  const [projects, setProjects] = useState([]);
  const [projectLoading, setProjectLoading] = useState(false);
  const [projectSearch, setProjectSearch] = useState("");

  const [unitOptions, setUnitOptions] = useState([]);
  const [sizeOptions, setSizeOptions] = useState([]);

  const [rowCategoryOptions, setRowCategoryOptions] = useState({});
  const [itemOptions, setItemOptions] = useState({});

  const [includeShipping, setIncludeShipping] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasDraft, setHasDraft] = useState(false);

  const [submitErrors, setSubmitErrors] = useState({});
  const [itemFieldErrors, setItemFieldErrors] = useState([]);
  const [reservingIndex, setReservingIndex] = useState(null);
  const [isBulkReserving, setIsBulkReserving] = useState(false);

  const [errors, setErrors] = useState({
    billing_pan: "",
    billing_gst: "",
    billing_email: "",
    billing_contact: "",
    billed_to_email: "",
    billed_to_contact: "",
    billed_to_pan_no: "",
    billed_to_gst_no: "",
    shipping_contact: "",
    shipping_phone: "",
  });

  const defaultTerms = getDefaultTerms(division);

  const [terms, setTerms] = useState(defaultTerms);

  const [formData, setFormData] = useState({
    po_number: "",
    supplier: "",
    division: division?.division_id || "",
    billing_division: division?.division_id || "",
    project: "",
    billing_name: "",
    billing_address: "",
    billing_pan: "",
    billing_gst: "",
    billing_email: "",
    billing_contact: "",
    billed_to_name: "",
    billed_to_email: "",
    billed_to_contact: "",
    billed_to_state_name: "",
    billed_to_state_code: "",
    billed_to_gst_no: "",
    billed_to_pan_no: "",
    billed_to_address: "",
    shipping_address: "",
    shipping_contact: "",
    shipping_phone: "",
    items: [createEmptyItem()],
    status: "pending",
    created_by: localStorage.getItem("user_id"),
  });

  const totalValue = formData.items.reduce(
    (sum, item) => sum + parseFloat(item.amount || 0),
    0
  );

  // ─────────────────────────────────────────────
  // Helpers
  // ─────────────────────────────────────────────

  const normalizeCompanyProfileIntoForm = (profile) => ({
    billed_to_name: profile?.company_name || "",
    billed_to_email: profile?.email || "",
    billed_to_contact: profile?.phone || "",
    billed_to_state_name: profile?.state || profile?.state_name || "",
    billed_to_state_code:
      profile?.state_code ||
      profile?.stateCode ||
      (profile?.gstin ? profile.gstin.substring(0, 2) : ""),
    billed_to_gst_no: profile?.gstin || profile?.gst || profile?.gst_no || "",
    billed_to_pan_no: profile?.pan || profile?.pan_no || "",
    billed_to_address: profile?.address || "",
  });

  const mapItemOption = (item) => ({
    id: item.id,
    name: item.name,
    item_code: item.item_code || item.code || "",
    unit_id: item.unit || item.uom || null,
    unit_name: item.unit_name || item.uom_name || "",
    size_id: item.size || null,
    size_name: item.size_name || "",
    rate: item.rate || item.unit_price || 0,
    description: item.description || item.item_description || "",
    current_stock: item.current_stock || 0,
  });

  const resolveSizeObj = (item, currentSizes = sizeOptions) => {
    let sizeObj = null;
    const rawSize = item.product_type || item.size;
    const sizeId =
      (typeof rawSize === "object" ? rawSize?.id : rawSize) ||
      item.size_id ||
      item.product_type_id ||
      null;
    const sizeName =
      (typeof rawSize === "object" ? rawSize?.size || rawSize?.name : "") ||
      item.size_name ||
      item.product_type_name ||
      item.type ||
      "";

    const sizes = Array.isArray(currentSizes) ? currentSizes : [];
    if (sizeId || sizeName) {
      const match = sizes.find(
        (s) =>
          (sizeId && String(s.id) === String(sizeId)) ||
          (sizeName &&
            String(s.size || s.name).trim().toLowerCase() ===
              String(sizeName).trim().toLowerCase())
      );
      if (match) {
        sizeObj = match;
      } else {
        sizeObj = {
          id: sizeId,
          size: sizeName || "",
          name: sizeName || "",
        };
      }
    }
    return sizeObj;
  };

  const normalizeProjectItem = (item, currentSizes = sizeOptions) => {
    const requiredQty = Number(item.quantity) || 0;
    const reservedQty = Number(item.reserved_qty) || 0;
    const issuedQty = Number(item.issued_qty) || 0;
    const currentStock = Number(item.current_stock) || 0;
    const reserveStock = Number(item.reserve_stock) || 0;
    const freeStock =
      item.free_stock !== undefined
        ? Number(item.free_stock)
        : Math.max(0, currentStock - reserveStock);
    const shortageQty =
      item.shortage_qty !== undefined
        ? Number(item.shortage_qty)
        : Math.max(0, requiredQty - reservedQty - issuedQty);
    const rate = Number(item.rate) || 0;

    const sizeObj = resolveSizeObj(item, currentSizes);

    return {
      line_id: item.id || null,
      size: sizeObj,
      category: item.category
        ? typeof item.category === "object"
          ? item.category
          : { id: item.category, name: item.category_name || "" }
        : null,
      sub_category: item.sub_category
        ? typeof item.sub_category === "object"
          ? item.sub_category
          : { subcat_id: item.sub_category, name: item.sub_category_name || "" }
        : null,
      item: item.item
        ? typeof item.item === "object"
          ? item.item
          : { id: item.item, name: item.item_name || "" }
        : null,
      description: item.description || "",
      required_qty: requiredQty,
      current_stock: currentStock,
      reserve_stock: reserveStock,
      free_stock: freeStock,
      mos_reserved_qty: reservedQty,
      reserve_input_qty: "",
      quantity: shortageQty,
      used_qty: Number(item.used_qty) || 0,
      reserve_qty: reservedQty,
      unit: item.unit || "",
      unit_name: item.uom || item.unit_name || "",
      rate: rate,
      amount: (shortageQty * rate).toFixed(2),
    };
  };

  const normalizeEditItem = (it, currentSizes = sizeOptions) => ({
    size: resolveSizeObj(it, currentSizes),
    category: it.category
      ? typeof it.category === "object"
        ? it.category
        : { id: it.category, name: it.category_name || "" }
      : null,
    sub_category: it.sub_category
      ? typeof it.sub_category === "object"
        ? it.sub_category
        : { subcat_id: it.sub_category, name: it.sub_category_name || "" }
      : null,
    item: it.item
      ? typeof it.item === "object"
        ? it.item
        : { id: it.item, name: it.item_name || "" }
      : null,
    unit: it.unit ?? it.uom ?? "",
    unit_name: it.unit_name ?? it.uom_name ?? "",
    description: it.description ?? it.item_name ?? "",
    quantity: parseFloat(it.quantity) || 0,
    used_qty: parseFloat(it.used_qty) || 0,
    reserve_qty: parseFloat(it.reserve_qty) || 0,
    rate: parseFloat(it.rate) || 0,
    amount: (
      (parseFloat(it.rate) || 0) * (parseFloat(it.quantity) || 0)
    ).toFixed(2),
  });

  const preloadRowOptions = useCallback(async (rows) => {
    const categoryMap = {};
    const itemMap = {};

    await Promise.all(
      rows.map(async (row, index) => {
        const sizeId =
          row?.size?.id || (typeof row?.size === "number" ? row.size : null);
        const categoryId =
          row?.category?.id || (typeof row?.category === "number" ? row.category : null);

        if (sizeId) {
          try {
            const catData = await getCategories(1, "", sizeId);
            categoryMap[index] = Array.isArray(catData)
              ? catData
              : catData?.results || [];
          } catch {
            categoryMap[index] = [];
          }
        } else if (categoryId) {
          try {
            const catData = await getCategories(1, "", "");
            categoryMap[index] = Array.isArray(catData)
              ? catData
              : catData?.results || [];
          } catch {
            categoryMap[index] = [];
          }
        }

        if (categoryId) {
          try {
            const itemData = await fetchItemsByCategoryAndSubcategory(categoryId, 0);
            itemMap[index] = (itemData?.results || []).map(mapItemOption);
          } catch {
            itemMap[index] = [];
          }
        }
      })
    );

    setRowCategoryOptions(categoryMap);
    setItemOptions(itemMap);
  }, []);

  // ─────────────────────────────────────────────
  // Fetch master data
  // ─────────────────────────────────────────────

  const fetchSuppliersList = async (page = 1, filters = {}) => {
    try {
      setSupplierLoading(true);
      const response = await fetchSuppliers(page, filters);
      const results = Array.isArray(response?.results)
        ? response.results
        : Array.isArray(response)
        ? response
        : [];

      setSuppliers(page === 1 ? results : (prev) => [...prev, ...results]);
    } catch {
      toast.error("Failed to load suppliers");
    } finally {
      setSupplierLoading(false);
    }
  };

  const fetchProjects = async (page = 1, filters = {}) => {
    try {
      setProjectLoading(true);
      const response = await ApproveSolistapi(page, filters);
      const results = Array.isArray(response?.results)
        ? response.results
        : Array.isArray(response)
        ? response
        : [];

      setProjects(page === 1 ? results : (prev) => [...prev, ...results]);
    } catch {
      toast.error("Failed to load project list");
    } finally {
      setProjectLoading(false);
    }
  };

  const fetchNextPoNo = async () => {
    try {
      const data = await GetNextPoNo();
      if (data?.po_number) {
        setFormData((prev) => ({ ...prev, po_number: data.po_number }));
      }
    } catch {
      toast.error("Failed to fetch PO number");
    }
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [unitRes, sizeRes, companyRes] = await Promise.all([
          getUnits(),
          getSizes(),
          !isEdit ? getCompanyProfile() : Promise.resolve(null),
        ]);

        setUnitOptions(Array.isArray(unitRes) ? unitRes : unitRes?.results || []);
        setSizeOptions(Array.isArray(sizeRes) ? sizeRes : sizeRes?.results || []);

        if (companyRes) {
          setCompanyProfile(companyRes);
          setFormData((prev) => ({
            ...prev,
            ...normalizeCompanyProfileIntoForm(companyRes),
          }));
        }
      } catch (error) {
        console.error("Initial data error:", error);
      }
    };

    fetchInitialData();
    fetchSuppliersList(1, {});
    fetchProjects(1, {});

    if (!isEdit && !sessionStorage.getItem("poDraft")) {
      fetchNextPoNo();
    }
  }, [isEdit]);

  useEffect(() => {
    const t = setTimeout(() => {
      fetchSuppliersList(1, { name: supplierSearch });
    }, 500);
    return () => clearTimeout(t);
  }, [supplierSearch]);

  useEffect(() => {
    const t = setTimeout(() => {
      fetchProjects(1, { project_name: projectSearch });
    }, 500);
    return () => clearTimeout(t);
  }, [projectSearch]);

  // ─────────────────────────────────────────────
  // Edit mode populate
  // ─────────────────────────────────────────────

  useEffect(() => {
    if (!isEdit || !poData) return;

    const mappedItems = (poData.items || []).map(normalizeEditItem);

    setFormData((prev) => ({
      ...prev,
      ...poData,
      created_by: localStorage.getItem("user_id"),
      billing_email: poData.billing_email || "",
      billing_contact: poData.billing_contact || "",
      billed_to_name: poData.billed_to_name ?? "",
      billed_to_email: poData.billed_to_email ?? "",
      billed_to_contact: poData.billed_to_contact ?? "",
      billed_to_state_name: poData.billed_to_state_name ?? "",
      billed_to_state_code: poData.billed_to_state_code ?? "",
      billed_to_gst_no: poData.billed_to_gst_no ?? "",
      billed_to_pan_no: poData.billed_to_pan_no ?? "",
      billed_to_address: poData.billed_to_address ?? "",
      status:
        poData.status === "recieved"
          ? "received"
          : poData.status || "pending",
      items: mappedItems.length ? mappedItems : [createEmptyItem()],
    }));

    setIncludeShipping(!!poData.shipping_address);
    setTerms(poData.terms_conditions || defaultTerms);

    preloadRowOptions(mappedItems);
  }, [isEdit, poData, preloadRowOptions]);

  // ─────────────────────────────────────────────
  // Draft Load
  // ─────────────────────────────────────────────

  useEffect(() => {
    if (isEdit) return;

    const draftForm = sessionStorage.getItem("poDraft");
    const draftTerms = sessionStorage.getItem("poTerms");
    if (!draftForm) return;

    try {
      const parsedForm = JSON.parse(draftForm);
      const parsedTerms = draftTerms ? JSON.parse(draftTerms) : defaultTerms;

      setFormData(parsedForm);
      setTerms(parsedTerms);
      setHasDraft(true);

      if (parsedForm?.items?.length) {
        preloadRowOptions(parsedForm.items);
      }

      // Always re-fetch a fresh PO number — the draft's number may be stale
      GetNextPoNo().then((data) => {
        if (data?.po_number) {
          setFormData((prev) => ({ ...prev, po_number: data.po_number }));
        }
      }).catch(() => {});

      toast.info("Loaded Purchase Order draft");
    } catch (e) {
      console.error("Draft parse error:", e);
    }
  }, [isEdit, preloadRowOptions]);


  // ─────────────────────────────────────────────
  // Top-level field handlers
  // ─────────────────────────────────────────────

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    if (submitErrors[field]) {
      setSubmitErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateField = (field, value) => {
    let error = "";

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
    const gstRegex =
      /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

    if (field === "billing_pan" || field === "billed_to_pan_no") {
      if (value && !panRegex.test(value.toUpperCase())) {
        error = "Invalid PAN format";
      }
    }

    if (
      field === "billing_gst" ||
      field === "billed_to_gst_no"
    ) {
      if (value && !gstRegex.test(value.toUpperCase())) {
        error = "Invalid GSTIN format";
      }
    }

    if (field === "billing_email" || field === "billed_to_email") {
      if (value && !emailRegex.test(value)) {
        error = "Invalid email format";
      }
    }

    if (
      field === "billing_contact" ||
      field === "billed_to_contact" ||
      field === "shipping_phone"
    ) {
      const cleanValue = value.replace(/[\s-]/g, "");
      const phoneRegex = /^[6-9]\d{9}$/;
      const landlineRegex = /^0\d{10,11}$/;
      const internationalRegex = /^\+\d{10,14}$/;

      if (
        value &&
        !phoneRegex.test(cleanValue) &&
        !landlineRegex.test(cleanValue) &&
        !internationalRegex.test(cleanValue)
      ) {
        error = "Invalid phone number";
      }
    }

    if (field === "shipping_contact") {
      if (value && !/^[a-zA-Z\s.'-]*$/.test(value)) {
        error = "Only letters and spaces allowed";
      }
    }

    setErrors((prev) => ({ ...prev, [field]: error }));
    return error;
  };

  // ─────────────────────────────────────────────
  // Project Change
  // ─────────────────────────────────────────────

  const handleProjectChange = async (newValue) => {
    if (!newValue) {
      setFormData((prev) => ({
        ...prev,
        project: "",
        billing_email: "",
        billing_contact: "",
        items: [createEmptyItem()],
      }));
      setItemOptions({});
      setRowCategoryOptions({});
      return;
    }

    let currentSizes = sizeOptions;
    if (!currentSizes || currentSizes.length === 0) {
      try {
        const sizeRes = await getSizes();
        currentSizes = Array.isArray(sizeRes) ? sizeRes : sizeRes?.results || [];
        setSizeOptions(currentSizes);
      } catch (e) {
        console.error("Error fetching size options:", e);
      }
    }

    const projectItems = (newValue.items || []).map((it) =>
      normalizeProjectItem(it, currentSizes)
    );

    setFormData((prev) => ({
      ...prev,
      project: newValue.id,
      billing_email: newValue.email || "",
      billing_contact: newValue.contact || newValue.phone || "",
      items: projectItems.length ? projectItems : [createEmptyItem()],
    }));

    await preloadRowOptions(projectItems);
  };

  // ─────────────────────────────────────────────
  // Stock Reservation & Release Handlers
  // ─────────────────────────────────────────────

  const handleInlineReserve = async (index) => {
    const item = formData.items[index];
    const qtyToReserve = parseFloat(item.reserve_input_qty);

    if (!qtyToReserve || qtyToReserve <= 0) {
      toast.warning("Please enter a valid reservation quantity > 0");
      return;
    }

    const availableFree = parseFloat(item.free_stock || 0);
    if (qtyToReserve > availableFree) {
      toast.error(
        `Cannot reserve more than available free stock (${availableFree})`
      );
      return;
    }

    const itemId =
      typeof item.item === "object" ? item.item?.id : item.item;
    const payload = item.line_id
      ? { line_id: item.line_id, qty: qtyToReserve }
      : { item_id: itemId, mos_id: formData.project, qty: qtyToReserve };

    if (!item.line_id && !formData.project) {
      toast.warning("No linked MOS / Project found for this item reservation.");
      return;
    }

    setReservingIndex(index);
    try {
      const res = await reserveMOSItemStock(payload);
      toast.success(
        res.message || `Reserved ${qtyToReserve} units successfully for MOS!`
      );

      const updatedItems = [...formData.items];
      const newReserved =
        res.reserved_qty !== undefined
          ? res.reserved_qty
          : (parseFloat(item.mos_reserved_qty) || 0) + qtyToReserve;
      const newFree =
        res.free_stock !== undefined
          ? res.free_stock
          : Math.max(0, availableFree - qtyToReserve);
      const newReserveStock =
        res.reserve_stock !== undefined
          ? res.reserve_stock
          : (parseFloat(item.reserve_stock) || 0) + qtyToReserve;

      // Dynamically adjust PO quantity: reduce by reserved amount (min 0)
      const currentPoQty = parseFloat(item.quantity) || 0;
      const newPoQty = Math.max(0, currentPoQty - qtyToReserve);
      const rate = parseFloat(item.rate) || 0;
      const newAmount = (newPoQty * rate).toFixed(2);

      updatedItems[index] = {
        ...item,
        mos_reserved_qty: newReserved,
        reserve_qty: newReserved,
        free_stock: newFree,
        reserve_stock: newReserveStock,
        line_id: res.line_id || item.line_id,
        quantity: newPoQty,
        amount: newAmount,
        reserve_input_qty: "",
      };

      setFormData((prev) => ({
        ...prev,
        items: updatedItems,
      }));
    } catch (err) {
      console.error("Error reserving stock:", err);
      toast.error(err.response?.data?.error || "Failed to reserve stock");
    } finally {
      setReservingIndex(null);
    }
  };

  const handleInlineRelease = async (index) => {
    const item = formData.items[index];
    const qtyToRelease =
      parseFloat(item.reserve_input_qty) ||
      parseFloat(item.mos_reserved_qty) ||
      0;

    if (qtyToRelease <= 0) {
      toast.warning("Please enter a valid quantity to release > 0");
      return;
    }

    const currentReserved = parseFloat(item.mos_reserved_qty) || 0;
    if (qtyToRelease > currentReserved) {
      toast.error(
        `Cannot release more than reserved quantity (${currentReserved})`
      );
      return;
    }

    const itemId =
      typeof item.item === "object" ? item.item?.id : item.item;
    const payload = item.line_id
      ? { line_id: item.line_id, qty: qtyToRelease }
      : { item_id: itemId, mos_id: formData.project, qty: qtyToRelease };

    setReservingIndex(index);
    try {
      const res = await releaseMOSItemStock(payload);
      toast.info(res.message || `Released ${qtyToRelease} reserved units.`);

      const updatedItems = [...formData.items];
      const newReserved =
        res.reserved_qty !== undefined
          ? res.reserved_qty
          : Math.max(0, currentReserved - qtyToRelease);
      const newFree =
        res.free_stock !== undefined
          ? res.free_stock
          : (parseFloat(item.free_stock) || 0) + qtyToRelease;
      const newReserveStock =
        res.reserve_stock !== undefined
          ? res.reserve_stock
          : Math.max(0, (parseFloat(item.reserve_stock) || 0) - qtyToRelease);

      // Dynamically adjust PO quantity: increase back by released amount
      const currentPoQty = parseFloat(item.quantity) || 0;
      const newPoQty = currentPoQty + qtyToRelease;
      const rate = parseFloat(item.rate) || 0;
      const newAmount = (newPoQty * rate).toFixed(2);

      updatedItems[index] = {
        ...item,
        mos_reserved_qty: newReserved,
        reserve_qty: newReserved,
        free_stock: newFree,
        reserve_stock: newReserveStock,
        quantity: newPoQty,
        amount: newAmount,
        reserve_input_qty: "",
      };

      setFormData((prev) => ({
        ...prev,
        items: updatedItems,
      }));
    } catch (err) {
      console.error("Error releasing stock:", err);
      toast.error(err.response?.data?.error || "Failed to release stock");
    } finally {
      setReservingIndex(null);
    }
  };

  const handleAutoFillReservations = () => {
    let filledCount = 0;
    const updatedItems = formData.items.map((item) => {
      const free = parseFloat(item.free_stock || 0);
      const needed =
        parseFloat(item.quantity || 0) > 0
          ? parseFloat(item.quantity || 0)
          : Math.max(
              0,
              (parseFloat(item.required_qty || 0)) -
                (parseFloat(item.mos_reserved_qty || 0))
            );
      if (free > 0 && needed > 0) {
        const canFill = Math.min(free, needed);
        filledCount++;
        return {
          ...item,
          reserve_input_qty: canFill,
        };
      }
      return item;
    });

    setFormData((prev) => ({ ...prev, items: updatedItems }));
    if (filledCount > 0) {
      toast.success(`Auto-filled reservation quantities for ${filledCount} item(s).`);
    } else {
      toast.info("No items with available free stock to reserve.");
    }
  };

  const handleClearAllReservations = () => {
    const updatedItems = formData.items.map((item) => ({
      ...item,
      reserve_input_qty: "",
    }));
    setFormData((prev) => ({ ...prev, items: updatedItems }));
    toast.info("Cleared all reserve quantities.");
  };

  const handleBulkReserve = async () => {
    const batchItems = [];
    formData.items.forEach((item, index) => {
      const qtyToReserve = parseFloat(item.reserve_input_qty);
      if (qtyToReserve && qtyToReserve > 0) {
        const itemId = typeof item.item === "object" ? item.item?.id : item.item;
        batchItems.push({
          index,
          line_id: item.line_id || null,
          item_id: itemId,
          mos_id: formData.project || null,
          qty: qtyToReserve,
        });
      }
    });

    if (batchItems.length === 0) {
      toast.warning("No reserve quantities entered. Use 'Auto Fill' or enter quantities first.");
      return;
    }

    setIsBulkReserving(true);
    try {
      const res = await reserveMOSItemStock({ items: batchItems });
      toast.success(res.message || `Successfully reserved stock for ${batchItems.length} items!`);

      const updatedItems = [...formData.items];
      (res.results || []).forEach((r) => {
        const idx = updatedItems.findIndex(
          (it) =>
            (r.line_id && it.line_id === r.line_id) ||
            (r.item_id && (typeof it.item === "object" ? it.item?.id : it.item) === r.item_id)
        );

        if (idx !== -1) {
          const item = updatedItems[idx];
          const reservedNow = r.reserved_now || parseFloat(item.reserve_input_qty) || 0;
          const currentPoQty = parseFloat(item.quantity) || 0;
          const newPoQty = Math.max(0, currentPoQty - reservedNow);
          const rate = parseFloat(item.rate) || 0;
          const newAmount = (newPoQty * rate).toFixed(2);

          updatedItems[idx] = {
            ...item,
            mos_reserved_qty: r.reserved_qty,
            reserve_qty: r.reserved_qty,
            free_stock: r.free_stock,
            reserve_stock: r.reserve_stock,
            quantity: newPoQty,
            amount: newAmount,
            reserve_input_qty: "",
          };
        }
      });

      setFormData((prev) => ({ ...prev, items: updatedItems }));
    } catch (err) {
      console.error("Bulk reserve error:", err);
      toast.error(err.response?.data?.error || "Failed to bulk reserve stock.");
    } finally {
      setIsBulkReserving(false);
    }
  };

  // ─────────────────────────────────────────────
  // Item Row Add/Delete
  // ─────────────────────────────────────────────

  const addItemRow = () => {
    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, createEmptyItem()],
    }));
  };

  const deleteItemRow = (index) => {
    const updatedItems = formData.items.filter((_, i) => i !== index);
    setFormData((prev) => ({
      ...prev,
      items: updatedItems.length ? updatedItems : [createEmptyItem()],
    }));

    if (itemFieldErrors[index]) {
      const newErrors = [...itemFieldErrors];
      delete newErrors[index];
      setItemFieldErrors(newErrors);
    }

    setItemOptions((prev) => {
      const next = {};
      Object.keys(prev).forEach((key) => {
        const k = Number(key);
        if (k < index) next[k] = prev[k];
        else if (k > index) next[k - 1] = prev[k];
      });
      return next;
    });

    setRowCategoryOptions((prev) => {
      const next = {};
      Object.keys(prev).forEach((key) => {
        const k = Number(key);
        if (k < index) next[k] = prev[k];
        else if (k > index) next[k - 1] = prev[k];
      });
      return next;
    });
  };

  // ─────────────────────────────────────────────
  // Row field direct text change
  // ─────────────────────────────────────────────

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...formData.items];
    updatedItems[index][field] = value;

    const qty = parseFloat(updatedItems[index].quantity) || 0;
    const rate = parseFloat(updatedItems[index].rate) || 0;
    updatedItems[index].amount = (qty * rate).toFixed(2);

    if (itemFieldErrors[index] && itemFieldErrors[index][field]) {
      const newItemErrors = [...itemFieldErrors];
      delete newItemErrors[index][field];
      if (Object.keys(newItemErrors[index]).length === 0) {
        delete newItemErrors[index];
      }
      setItemFieldErrors(newItemErrors);
    }

    setFormData((prev) => ({ ...prev, items: updatedItems }));
  };

  // ─────────────────────────────────────────────
  // Row autocomplete/select change
  // ─────────────────────────────────────────────

  const handleInputChange = async (index, key, value) => {
    const updatedRows = [...formData.items];
    let row = { ...updatedRows[index] };

    if (itemFieldErrors[index] && itemFieldErrors[index][key]) {
      const newItemErrors = [...itemFieldErrors];
      delete newItemErrors[index][key];
      if (Object.keys(newItemErrors[index]).length === 0) {
        delete newItemErrors[index];
      }
      setItemFieldErrors(newItemErrors);
    }

    switch (key) {
      case "size":
        row = {
          ...row,
          size: value,
          category: null,
          item: null,
          description: "",
          unit: "",
          unit_name: "",
          rate: "",
          amount: "",
        };

        if (value?.id) {
          try {
            const catData = await getCategories(1, "", value.id);
            const cats = Array.isArray(catData)
              ? catData
              : catData?.results || [];
            setRowCategoryOptions((prev) => ({ ...prev, [index]: cats }));
          } catch {
            setRowCategoryOptions((prev) => ({ ...prev, [index]: [] }));
          }
        } else {
          setRowCategoryOptions((prev) => ({ ...prev, [index]: [] }));
        }

        setItemOptions((prev) => ({ ...prev, [index]: [] }));
        break;

      case "category":
        row = {
          ...row,
          category: value,
          item: null,
          description: "",
          unit: "",
          unit_name: "",
          rate: "",
          amount: "",
        };

        if (value?.id) {
          const itemData = await fetchItemsByCategoryAndSubcategory(
            value.id,
            0,
            1,
            ""
          );
          const mappedItems = (itemData.results || []).map(mapItemOption);
          setItemOptions((prev) => ({ ...prev, [index]: mappedItems }));
        } else {
          setItemOptions((prev) => ({ ...prev, [index]: [] }));
        }
        break;

      case "item": {
        const selectedItem = value;
        if (selectedItem) {
          const unitObj = selectedItem?.unit_id
            ? unitOptions.find(
                (u) => String(u.id) === String(selectedItem.unit_id)
              )
            : null;

          const sizeObj = selectedItem?.size_id
            ? sizeOptions.find(
                (s) => String(s.id) === String(selectedItem.size_id)
              )
            : null;

          row = {
            ...row,
            item: selectedItem,
            description: selectedItem?.description || selectedItem?.name || "",
            rate: selectedItem?.rate || selectedItem?.price || "",
            unit: unitObj?.id || selectedItem?.unit_id || "",
            unit_name: unitObj?.name || selectedItem?.unit_name || "",
            size: sizeObj || row.size,
          };

          const qty = parseFloat(row.quantity) || 0;
          const rate = parseFloat(row.rate) || 0;
          row.amount = (qty * rate).toFixed(2);
        } else {
          row = {
            ...row,
            item: null,
            description: "",
            rate: "",
            amount: "",
          };
        }
        break;
      }

      case "unit":
        if (value && typeof value === "object") {
          row = {
            ...row,
            unit: value.id || "",
            unit_name: value.name || value.unit_name || "",
          };
        } else {
          row = { ...row, unit: value || "", unit_name: "" };
        }
        break;

      default:
        row = { ...row, [key]: value };
    }

    updatedRows[index] = row;
    setFormData((prev) => ({ ...prev, items: updatedRows }));
  };

  // ─────────────────────────────────────────────
  // Debounced search handlers
  // ─────────────────────────────────────────────

  const handleItemInputChange = useCallback(
    debounce(async (index, row, newValue, type) => {
      if (type === "category") {
        const productTypeId =
          row.size?.id || (typeof row.size === "number" ? row.size : null);

        if (!productTypeId) {
          setRowCategoryOptions((prev) => ({ ...prev, [index]: [] }));
          return;
        }

        const catData = await getCategories(
          1,
          newValue?.trim() || "",
          productTypeId
        );
        const list = Array.isArray(catData?.results) ? catData.results : catData;
        setRowCategoryOptions((prev) => ({ ...prev, [index]: list || [] }));
        return;
      }

      if (type === "item" && row.category) {
        const categoryId = row.category?.id || row.category;
        const itemData = await fetchItemsByCategoryAndSubcategory(
          categoryId,
          0,
          1,
          newValue || ""
        );

        const mappedItems = (itemData.results || []).map(mapItemOption);
        setItemOptions((prev) => ({
          ...prev,
          [index]: mappedItems,
        }));
        return;
      }

      if (type === "size") {
        const sizeData = await getSizes(newValue || "");
        const list = Array.isArray(sizeData?.results)
          ? sizeData.results
          : sizeData;
        setSizeOptions(list || []);
      }
    }, 500),
    [unitOptions, sizeOptions]
  );

  // ─────────────────────────────────────────────
  // Terms
  // ─────────────────────────────────────────────

  const handleTermChange = (index, field, value) => {
    const updatedTerms = [...terms];
    updatedTerms[index][field] = value;
    setTerms(updatedTerms);
  };

  const addTerm = () => {
    setTerms([...terms, { key: "", value: "", is_default: false }]);
  };

  const removeTerm = (index) => {
    setTerms(terms.filter((_, i) => i !== index));
  };

  // ─────────────────────────────────────────────
  // Validation
  // ─────────────────────────────────────────────

  const validateForm = () => {
    const newErrors = {};
    const newItemErrors = [];

    if (!formData.po_number) newErrors.po_number = "PO Number is required";
    if (!formData.supplier) newErrors.supplier = "Supplier is required";
    if (!formData.billing_name) newErrors.billing_name = "Supplier Name is required";
    if (!formData.billing_address) newErrors.billing_address = "Supplier Address is required";
    if (!formData.billed_to_name) newErrors.billed_to_name = "Company Name is required";
    if (!formData.billed_to_address) newErrors.billed_to_address = "Address is required";

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
    const gstRegex =
      /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    const phoneRegex = /^[6-9]\d{9}$/;
    const internationalRegex = /^\+\d{10,14}$/;

    if (formData.billed_to_email && !emailRegex.test(formData.billed_to_email)) {
      newErrors.billed_to_email = "Invalid email format";
    }
    if (formData.billing_email && !emailRegex.test(formData.billing_email)) {
      newErrors.billing_email = "Invalid email format";
    }

    if (formData.billing_contact) {
      const cleanPhone = formData.billing_contact.replace(/[\s-]/g, "");
      if (!phoneRegex.test(cleanPhone) && !internationalRegex.test(cleanPhone)) {
        newErrors.billing_contact = "Invalid phone number format";
      }
    }

    if (formData.billed_to_contact) {
      const cleanPhone = formData.billed_to_contact.replace(/[\s-]/g, "");
      if (!phoneRegex.test(cleanPhone) && !internationalRegex.test(cleanPhone)) {
        newErrors.billed_to_contact = "Invalid phone number format";
      }
    }

    if (formData.billing_pan && !panRegex.test(formData.billing_pan.toUpperCase())) {
      newErrors.billing_pan = "Invalid PAN format";
    }

    if (formData.billing_gst && !gstRegex.test(formData.billing_gst.toUpperCase())) {
      newErrors.billing_gst = "Invalid GSTIN format";
    }

    if (
      formData.billed_to_pan_no &&
      !panRegex.test(formData.billed_to_pan_no.toUpperCase())
    ) {
      newErrors.billed_to_pan_no = "Invalid PAN format";
    }

    if (
      formData.billed_to_gst_no &&
      !gstRegex.test(formData.billed_to_gst_no.toUpperCase())
    ) {
      newErrors.billed_to_gst_no = "Invalid GSTIN format";
    }

    if (includeShipping && formData.shipping_phone) {
      const cleanPhone = formData.shipping_phone.replace(/[\s-]/g, "");
      if (!phoneRegex.test(cleanPhone) && !internationalRegex.test(cleanPhone)) {
        newErrors.shipping_phone = "Invalid phone number format";
      }
    }

    if (!formData.items.length) {
      newErrors.items = "At least one item is required";
    } else {
      let hasItemsToOrder = false;
      let allItemsFulfilled = true;

      formData.items.forEach((item, index) => {
        const itemError = {};
        let hasError = false;
        const qty = parseFloat(item.quantity || 0);
        const isFulfilledFromStock =
          qty <= 0 &&
          (parseFloat(item.mos_reserved_qty || 0) > 0 ||
            parseFloat(item.reserve_qty || 0) > 0);

        if (!item.size) {
          itemError.size = "Product Type is required";
          hasError = true;
        }
        if (!item.category) {
          itemError.category = "Category is required";
          hasError = true;
        }
        if (!item.item) {
          itemError.item = "Item is required";
          hasError = true;
        }
        if (qty <= 0) {
          if (!isFulfilledFromStock) {
            itemError.quantity = "Valid quantity is required";
            hasError = true;
          }
        } else {
          hasItemsToOrder = true;
          allItemsFulfilled = false;
        }
        if (!item.unit) {
          itemError.unit = "Unit is required";
          hasError = true;
        }
        if (qty > 0 && (item.rate === "" || parseFloat(item.rate) < 0)) {
          itemError.rate = "Valid rate is required";
          hasError = true;
        }

        if (hasError) {
          newItemErrors[index] = itemError;
        }
      });

      if (!hasItemsToOrder && allItemsFulfilled && formData.items.length > 0) {
        newErrors.items =
          "All items in this project are already 100% fulfilled from warehouse stock. No Purchase Order is needed for supplier.";
      } else if (!hasItemsToOrder && formData.items.length > 0) {
        newErrors.items = "At least one item with purchase quantity > 0 is required.";
      }
    }

    return { fieldErrors: newErrors, itemErrors: newItemErrors };
  };

  // ─────────────────────────────────────────────
  // Submit
  // ─────────────────────────────────────────────

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) {
      toast.warn("Please wait, form is already submitting...");
      return;
    }

    setSubmitErrors({});
    setItemFieldErrors([]);

    const validation = validateForm();

    if (
      Object.keys(validation.fieldErrors).length > 0 ||
      validation.itemErrors.length > 0
    ) {
      setSubmitErrors(validation.fieldErrors);
      setItemFieldErrors(validation.itemErrors);

      setTimeout(() => {
        const firstErrorElement =
          document.querySelector(".Mui-error") ||
          document.querySelector('input[style*="border: 1px solid red"]');
        if (firstErrorElement) {
          firstErrorElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 100);

      toast.error("Please fix all validation errors before submitting");
      return;
    }

    setIsSubmitting(true);

    try {
      const filteredTerms = terms.filter(
        (term) => term.key?.trim() !== "" && term.value?.trim() !== ""
      );

      const cleanedItems = formData.items
        .filter((item) => parseFloat(item.quantity || 0) > 0)
        .map((item) => ({
          ...item,
          category: item.category?.id || item.category || null,
          sub_category: item.sub_category?.subcat_id || item.sub_category || null,
          item: item.item?.id || item.item || null,
          size: typeof item.size === "object" ? item.size?.id || null : item.size || null,
          unit: typeof item.unit === "object" ? item.unit?.id || null : item.unit || null,
          quantity: parseFloat(item.quantity) || 0,
          reserve_qty: parseFloat(item.reserve_qty) || 0,
          rate: parseFloat(item.rate) || 0,
          amount: parseFloat(item.amount) || 0,
          description: item.description || "",
          used_qty: parseFloat(item.used_qty) || 0,
        }));

      const rawStatus = String(formData.status || "pending").toLowerCase();
      const status = rawStatus === "recieved" ? "received" : rawStatus;

      const payload = {
        ...formData,
        status,
        items: cleanedItems,
        include_shipping: includeShipping,
        terms_conditions: filteredTerms,
        quotation_date: formData.quotation_date
          ? dayjs(formData.quotation_date).format("YYYY-MM-DD")
          : null,
      };

      if (isEdit && poData?.po_id) {
        await updatepos(poData.po_id, payload);
        toast.success("Purchase Order updated successfully!");
      } else {
        await postpos(payload);
        sessionStorage.removeItem("poDraft");
        sessionStorage.removeItem("poTerms");
        toast.success("Purchase Order created successfully!");
      }

      setTimeout(() => {
        navigate("/dashboard/po-list");
      }, 1200);
    } catch (err) {
      console.error("Submit error:", err);

      let errorMessage = "Failed to submit Purchase Order";
      if (err.response?.data) {
        if (typeof err.response.data === "object") {
          errorMessage = Object.values(err.response.data).flat().join(", ");
        } else {
          errorMessage = err.response.data;
        }
      } else if (err.request) {
        errorMessage = "No response from server. Please check your connection.";
      }

      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─────────────────────────────────────────────
  // Draft
  // ─────────────────────────────────────────────

  const handleSaveDraft = () => {
    if (isEdit) return;
    sessionStorage.setItem("poDraft", JSON.stringify(formData));
    sessionStorage.setItem("poTerms", JSON.stringify(terms));
    setHasDraft(true);
    toast.info("Purchase Order draft saved!");
  };

  const handleDeleteDraft = () => {
    sessionStorage.removeItem("poDraft");
    sessionStorage.removeItem("poTerms");
    setHasDraft(false);
    setIncludeShipping(false);
    setTerms(defaultTerms);
    setSubmitErrors({});
    setItemFieldErrors([]);
    setItemOptions({});
    setRowCategoryOptions({});

    setFormData((prev) => ({
      ...prev,
      supplier: "",
      project: "",
      billing_name: "",
      billing_address: "",
      billing_pan: "",
      billing_gst: "",
      billing_email: "",
      billing_contact: "",
      ...normalizeCompanyProfileIntoForm(companyProfile || {}),
      shipping_address: "",
      shipping_contact: "",
      shipping_phone: "",
      items: [createEmptyItem()],
      status: "pending",
      po_number: prev.po_number,
      division: division?.division_id || "",
      billing_division: division?.division_id || "",
      created_by: localStorage.getItem("user_id"),
    }));

    toast.info("Draft deleted");
  };

  return (
    <Box sx={{ px: 0, pt: 0, mt: 1, maxWidth: 1200, mx: "auto" }}>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />

      {/* Top Buttons */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "right",
          alignItems: "center",
          mb: 3,
          flexDirection: isMobile ? "column" : "row",
          gap: isMobile ? 2 : 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexDirection: isMobile ? "column" : "row",
            width: isMobile ? "100%" : "auto",
          }}
        >
          <Button
            type="button"
            variant="outlined"
            onClick={hasDraft ? handleDeleteDraft : handleSaveDraft}
            fullWidth={isMobile}
            sx={{
              height: 40,
              fontSize: "0.875rem",
              color: hasDraft ? "#d32f2f" : "#ff8400ff",
              borderColor: hasDraft ? "#d32f2f" : "#ff8400ff",
              fontWeight: 500,
              textTransform: "none",
              minWidth: 120,
              borderRadius: 0,
            }}
          >
            {hasDraft ? "Delete Draft" : "Save Draft"}
          </Button>

          <Button
            type="button"
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
            fullWidth={isMobile}
            sx={{
              height: 40,
              fontSize: "0.875rem",
              fontWeight: 600,
              textTransform: "none",
              minWidth: 180,
              borderRadius: 0,
            }}
          >
            {isSubmitting ? "Submitting..." : "Submit Purchase Order"}
          </Button>
        </Box>
      </Box>

      {/* Header Form */}
      <Box border="1px solid #ccc" borderRadius="8px" sx={{ mb: 3, p: isMobile ? 1.5 : 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="PO Number"
              required
              value={formData.po_number || ""}
              onChange={handleChange("po_number")}
              error={!!submitErrors.po_number}
              helperText={submitErrors.po_number}
              disabled
              InputProps={{
                sx: { height: 45, fontSize: "0.875rem", borderRadius: 1 },
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Autocomplete
              options={Array.isArray(projects) ? projects : []}
              getOptionLabel={(option) => option?.project_name || `Project ${option?.id}` || ""}
              value={
                Array.isArray(projects) && formData.project
                  ? projects.find((i) => i.id === formData.project) || null
                  : null
              }
              loading={projectLoading}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              onChange={(e, newValue) => {
                handleProjectChange(newValue);
                if (submitErrors.project) {
                  setSubmitErrors((prev) => ({ ...prev, project: "" }));
                }
              }}
              onInputChange={(e, newInputValue, reason) => {
                if (reason === "input") setProjectSearch(newInputValue);
              }}
              noOptionsText="No projects found"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="MOS"
                  fullWidth
                  error={!!submitErrors.project}
                  helperText={submitErrors.project}
                  InputProps={{
                    ...params.InputProps,
                    sx: { height: 45, fontSize: "0.875rem", borderRadius: 1 },
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label="Status"
              value={formData.status || "pending"}
              onChange={handleChange("status")}
              SelectProps={{ native: true }}
              InputProps={{
                sx: { height: 45, fontSize: "0.875rem", borderRadius: 1 },
              }}
            >
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="received">Received</option>
              <option value="cancelled">Cancelled</option>
            </TextField>
          </Grid>
        </Grid>
      </Box>

      {/* Billed From / Billed To */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Billed From */}
        <Grid item xs={12} md={6}>
          <Box border="1px solid #ccc" borderRadius="8px" sx={{ p: isMobile ? 1.5 : 2, height: "100%" }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              Billed From
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Autocomplete
                  options={suppliers}
                  getOptionLabel={(option) => option.name || ""}
                  value={
                    Array.isArray(suppliers)
                      ? suppliers.find((s) => s.id === formData.supplier) || null
                      : null
                  }
                  loading={supplierLoading}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  onChange={(e, newValue) => {
                    if (newValue) {
                      setFormData((prev) => ({
                        ...prev,
                        supplier: newValue.id,
                        billing_name: newValue.name || "",
                        billing_address: newValue.address || "",
                        billing_pan: newValue.pan || "",
                        billing_gst: newValue.gst || "",
                        billing_email: newValue.email || "",
                        billing_contact: newValue.contact || "",
                      }));

                      setErrors((prev) => ({
                        ...prev,
                        billing_email: "",
                        billing_contact: "",
                        billing_pan: "",
                        billing_gst: "",
                      }));
                    } else {
                      setFormData((prev) => ({
                        ...prev,
                        supplier: "",
                        billing_name: "",
                        billing_address: "",
                        billing_pan: "",
                        billing_gst: "",
                        billing_email: "",
                        billing_contact: "",
                      }));
                    }

                    if (submitErrors.supplier) {
                      setSubmitErrors((prev) => ({ ...prev, supplier: "" }));
                    }
                  }}
                  onInputChange={(e, value) => setSupplierSearch(value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Supplier"
                      fullWidth
                      error={!!submitErrors.supplier}
                      helperText={submitErrors.supplier}
                      InputProps={{
                        ...params.InputProps,
                        sx: { height: 45, fontSize: "0.875rem", borderRadius: 1 },
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Supplier Name"
                  required
                  value={formData.billing_name || ""}
                  onChange={handleChange("billing_name")}
                  error={!!submitErrors.billing_name}
                  helperText={submitErrors.billing_name}
                  InputProps={{
                    sx: { height: 45, fontSize: "0.875rem", borderRadius: 1 },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.billing_email || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData((prev) => ({ ...prev, billing_email: value }));
                    validateField("billing_email", value);
                    if (submitErrors.billing_email) {
                      setSubmitErrors((prev) => ({ ...prev, billing_email: "" }));
                    }
                  }}
                  error={!!errors.billing_email || !!submitErrors.billing_email}
                  helperText={errors.billing_email || submitErrors.billing_email}
                  InputProps={{
                    sx: { height: 45, fontSize: "0.875rem", borderRadius: 1 },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Contact"
                  value={formData.billing_contact || ""}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9+\s-]/g, "");
                    setFormData((prev) => ({ ...prev, billing_contact: value }));
                    validateField("billing_contact", value);
                    if (submitErrors.billing_contact) {
                      setSubmitErrors((prev) => ({ ...prev, billing_contact: "" }));
                    }
                  }}
                  error={!!errors.billing_contact || !!submitErrors.billing_contact}
                  helperText={errors.billing_contact || submitErrors.billing_contact}
                  InputProps={{
                    sx: { height: 45, fontSize: "0.875rem", borderRadius: 1 },
                  }}
                  inputProps={{ maxLength: 15 }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Supplier Address"
                  required
                  multiline
                  rows={2}
                  value={formData.billing_address || ""}
                  onChange={handleChange("billing_address")}
                  error={!!submitErrors.billing_address}
                  helperText={submitErrors.billing_address}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="PAN"
                  value={formData.billing_pan || ""}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase();
                    setFormData((prev) => ({ ...prev, billing_pan: value }));
                    validateField("billing_pan", value);
                    if (submitErrors.billing_pan) {
                      setSubmitErrors((prev) => ({ ...prev, billing_pan: "" }));
                    }
                  }}
                  error={!!errors.billing_pan || !!submitErrors.billing_pan}
                  helperText={errors.billing_pan || submitErrors.billing_pan}
                  InputProps={{
                    sx: { height: 45, fontSize: "0.875rem", borderRadius: 1 },
                  }}
                  inputProps={{ maxLength: 10 }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="GST"
                  value={formData.billing_gst || ""}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase();
                    setFormData((prev) => ({ ...prev, billing_gst: value }));
                    validateField("billing_gst", value);
                    if (submitErrors.billing_gst) {
                      setSubmitErrors((prev) => ({ ...prev, billing_gst: "" }));
                    }
                  }}
                  error={!!errors.billing_gst || !!submitErrors.billing_gst}
                  helperText={errors.billing_gst || submitErrors.billing_gst}
                  InputProps={{
                    sx: { height: 45, fontSize: "0.875rem", borderRadius: 1 },
                  }}
                  inputProps={{ maxLength: 15 }}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Billed To */}
        <Grid item xs={12} md={6}>
          <Box border="1px solid #ccc" borderRadius="8px" sx={{ p: isMobile ? 1.5 : 2, height: "100%" }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              Billed To
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Company Name"
                  required
                  value={formData.billed_to_name || ""}
                  onChange={handleChange("billed_to_name")}
                  error={!!submitErrors.billed_to_name}
                  helperText={submitErrors.billed_to_name}
                  InputProps={{
                    sx: { height: 45, fontSize: "0.875rem", borderRadius: 1 },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.billed_to_email || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData((prev) => ({ ...prev, billed_to_email: value }));
                    validateField("billed_to_email", value);
                    if (submitErrors.billed_to_email) {
                      setSubmitErrors((prev) => ({ ...prev, billed_to_email: "" }));
                    }
                  }}
                  error={!!errors.billed_to_email || !!submitErrors.billed_to_email}
                  helperText={errors.billed_to_email || submitErrors.billed_to_email}
                  InputProps={{
                    sx: { height: 45, fontSize: "0.875rem", borderRadius: 1 },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={formData.billed_to_contact || ""}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9+\s-]/g, "");
                    setFormData((prev) => ({ ...prev, billed_to_contact: value }));
                    validateField("billed_to_contact", value);
                  }}
                  error={!!errors.billed_to_contact}
                  helperText={errors.billed_to_contact}
                  InputProps={{
                    sx: { height: 45, fontSize: "0.875rem", borderRadius: 1 },
                  }}
                  inputProps={{ maxLength: 15 }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="State Name"
                  value={formData.billed_to_state_name || ""}
                  onChange={handleChange("billed_to_state_name")}
                  InputProps={{
                    sx: { height: 45, fontSize: "0.875rem", borderRadius: 1 },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="State Code"
                  value={formData.billed_to_state_code || ""}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, "");
                    setFormData((prev) => ({ ...prev, billed_to_state_code: value }));
                  }}
                  InputProps={{
                    sx: { height: 45, fontSize: "0.875rem", borderRadius: 1 },
                  }}
                  inputProps={{ maxLength: 2 }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="GST No"
                  value={formData.billed_to_gst_no || ""}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase();
                    setFormData((prev) => ({ ...prev, billed_to_gst_no: value }));
                    validateField("billed_to_gst_no", value);
                    if (submitErrors.billed_to_gst_no) {
                      setSubmitErrors((prev) => ({ ...prev, billed_to_gst_no: "" }));
                    }
                  }}
                  error={!!errors.billed_to_gst_no || !!submitErrors.billed_to_gst_no}
                  helperText={errors.billed_to_gst_no || submitErrors.billed_to_gst_no}
                  InputProps={{
                    sx: { height: 45, fontSize: "0.875rem", borderRadius: 1 },
                  }}
                  inputProps={{ maxLength: 15 }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="PAN No"
                  value={formData.billed_to_pan_no || ""}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase();
                    setFormData((prev) => ({ ...prev, billed_to_pan_no: value }));
                    validateField("billed_to_pan_no", value);
                    if (submitErrors.billed_to_pan_no) {
                      setSubmitErrors((prev) => ({ ...prev, billed_to_pan_no: "" }));
                    }
                  }}
                  error={!!errors.billed_to_pan_no || !!submitErrors.billed_to_pan_no}
                  helperText={errors.billed_to_pan_no || submitErrors.billed_to_pan_no}
                  InputProps={{
                    sx: { height: 45, fontSize: "0.875rem", borderRadius: 1 },
                  }}
                  inputProps={{ maxLength: 10 }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  required
                  multiline
                  rows={2}
                  value={formData.billed_to_address || ""}
                  onChange={handleChange("billed_to_address")}
                  error={!!submitErrors.billed_to_address}
                  helperText={submitErrors.billed_to_address}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>

      {/* Shipping */}
      <Box sx={{ mb: 3 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={includeShipping}
              onChange={(e) => setIncludeShipping(e.target.checked)}
            />
          }
          label="Include Shipping Details"
        />
        {includeShipping && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Shipping Address"
                value={formData.shipping_address || ""}
                onChange={handleChange("shipping_address")}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Contact Person"
                value={formData.shipping_contact || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData((prev) => ({ ...prev, shipping_contact: value }));
                  validateField("shipping_contact", value);
                }}
                error={!!errors.shipping_contact}
                helperText={errors.shipping_contact}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.shipping_phone || ""}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9+\s-]/g, "");
                  setFormData((prev) => ({ ...prev, shipping_phone: value }));
                  validateField("shipping_phone", value);
                }}
                error={!!errors.shipping_phone || !!submitErrors.shipping_phone}
                helperText={errors.shipping_phone || submitErrors.shipping_phone}
                inputProps={{ maxLength: 15 }}
              />
            </Grid>
          </Grid>
        )}
      </Box>

      {/* Items Error */}
      {submitErrors.items && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            bgcolor: "#fff3cd",
            border: "1px solid #ffeaa7",
            borderRadius: 1,
          }}
        >
          <Typography color="error" variant="body2">
            {submitErrors.items}
          </Typography>
        </Box>
      )}

      {/* Reservation Bulk Actions Toolbar */}
      {formData.project && formData.items.length > 0 && (
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            bgcolor: (t) =>
              t.palette.mode === "dark" ? alpha("#1976d2", 0.1) : "#f0f7ff",
            border: "1px solid",
            borderColor: (t) =>
              t.palette.mode === "dark" ? alpha("#1976d2", 0.3) : "#b3d7ff",
            borderRadius: "8px",
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LockIcon sx={{ color: "#1976d2", fontSize: 20 }} />
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, color: "#0d47a1" }}
            >
              Warehouse Stock Reservation
            </Typography>
            {formData.items.some(
              (it) => parseFloat(it.reserve_input_qty) > 0
            ) && (
              <Chip
                size="small"
                label={`${
                  formData.items.filter(
                    (it) => parseFloat(it.reserve_input_qty) > 0
                  ).length
                } item(s) ready to reserve`}
                color="primary"
                sx={{ height: 22, fontSize: "0.72rem", fontWeight: 600 }}
              />
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={handleAutoFillReservations}
              startIcon={<FlashOnIcon />}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                height: 32,
                fontSize: "0.8rem",
              }}
            >
              Auto Fill Available
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              onClick={handleClearAllReservations}
              startIcon={<ClearAllIcon />}
              sx={{
                textTransform: "none",
                fontWeight: 500,
                height: 32,
                fontSize: "0.8rem",
              }}
            >
              Clear All
            </Button>
            <Button
              size="small"
              variant="contained"
              color="success"
              disabled={
                isBulkReserving ||
                !formData.items.some(
                  (it) => parseFloat(it.reserve_input_qty) > 0
                )
              }
              onClick={handleBulkReserve}
              startIcon={
                isBulkReserving ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <LockIcon sx={{ fontSize: 16 }} />
                )
              }
              sx={{
                textTransform: "none",
                fontWeight: 700,
                height: 32,
                fontSize: "0.8rem",
                boxShadow: "none",
              }}
            >
              {isBulkReserving ? "Reserving..." : "Reserve All Entered"}
            </Button>
          </Box>
        </Box>
      )}

      {/* Items */}
      {isMobile ? (
        <Box sx={{ mt: 2 }}>
          {formData.items.map((item, index) => (
            <Box
              key={index}
              sx={{
                mb: 3,
                p: 2,
                border: "1px solid #ccc",
                borderRadius: "8px",
                backgroundColor: "#fafafa",
                borderColor: itemFieldErrors[index] ? "error.main" : "#ccc",
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                <strong>Item #{index + 1}</strong>
                <IconButton onClick={() => deleteItemRow(index)} color="error" size="small">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>

              {/* Product Type */}
              <Autocomplete
                size="small"
                options={Array.isArray(sizeOptions) ? sizeOptions : []}
                getOptionLabel={(option) => option?.size || option?.name || ""}
                value={
                  item.size
                    ? typeof item.size === "object"
                      ? (Array.isArray(sizeOptions)
                          ? sizeOptions.find(
                              (s) =>
                                (item.size.id && String(s.id) === String(item.size.id)) ||
                                (s.size &&
                                  item.size.size &&
                                  String(s.size).trim().toLowerCase() ===
                                    String(item.size.size).trim().toLowerCase())
                            )
                          : null) || item.size
                      : (Array.isArray(sizeOptions)
                          ? sizeOptions.find((s) => String(s.id) === String(item.size))
                          : null) || null
                    : null
                }
                onChange={(e, newValue) => handleInputChange(index, "size", newValue)}
                onInputChange={(e, newInputValue, reason) => {
                  if (reason === "input") {
                    handleItemInputChange(index, item, newInputValue, "size");
                  }
                }}
                isOptionEqualToValue={(option, value) =>
                  String(option?.id) === String(value?.id) ||
                  (option?.size &&
                    value?.size &&
                    String(option.size).trim().toLowerCase() ===
                      String(value.size).trim().toLowerCase())
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Product Type"
                    size="small"
                    fullWidth
                    error={!!itemFieldErrors[index]?.size}
                    helperText={itemFieldErrors[index]?.size}
                    sx={{ mb: 2 }}
                  />
                )}
              />

              {/* Category */}
              <Autocomplete
                options={rowCategoryOptions[index] || []}
                getOptionLabel={(opt) => opt.name || ""}
                isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                value={item.category || null}
                disabled={!item.size}
                onChange={(e, val, reason) => {
                  if (reason === "clear" || !val) {
                    handleInputChange(index, "category", null);
                    return;
                  }
                  handleInputChange(index, "category", val);
                }}
                onInputChange={(e, newValue, reason) => {
                  if (reason === "input") {
                    handleItemInputChange(index, item, newValue, "category");
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Category"
                    size="small"
                    fullWidth
                    required
                    error={!!itemFieldErrors[index]?.category}
                    helperText={
                      !item.size
                        ? "Select Product Type first"
                        : itemFieldErrors[index]?.category
                    }
                    sx={{ mb: 2 }}
                  />
                )}
              />

              {/* Item */}
              <Autocomplete
                options={Array.isArray(itemOptions[index]) ? itemOptions[index] : []}
                getOptionLabel={(opt) => opt.name || ""}
                isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                value={item.item || null}
                disabled={!item.category}
                onChange={(e, val, reason) => {
                  if (reason === "clear" || !val) {
                    handleInputChange(index, "item", null);
                    return;
                  }
                  handleInputChange(index, "item", val);
                }}
                onInputChange={(e, newValue) =>
                  handleItemInputChange(index, item, newValue, "item")
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    fullWidth
                    label="Item"
                    required
                    error={!!itemFieldErrors[index]?.item}
                    helperText={itemFieldErrors[index]?.item}
                    sx={{ mb: 2 }}
                  />
                )}
              />

              <TextField
                label="Description"
                fullWidth
                size="small"
                value={item.description}
                onChange={(e) => handleItemChange(index, "description", e.target.value)}
                sx={{ mb: 2 }}
              />

              {/* Stock & Reservation Breakdown */}
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: (t) =>
                    t.palette.mode === "dark"
                      ? alpha(t.palette.background.paper, 0.6)
                      : "#f9f9f9",
                  border: "1px solid #e0e0e0",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    MOS Required:
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    {item.required_qty || item.quantity || 0}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    Free Warehouse Stock:
                  </Typography>
                  <Chip
                    size="small"
                    label={`${item.free_stock || 0}`}
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.75rem",
                      height: 22,
                      bgcolor:
                        (item.free_stock || 0) > 0
                          ? alpha("#2e7d32", 0.12)
                          : alpha("#d32f2f", 0.08),
                      color:
                        (item.free_stock || 0) > 0 ? "#2e7d32" : "#d32f2f",
                      border: `1px solid ${
                        (item.free_stock || 0) > 0
                          ? alpha("#2e7d32", 0.3)
                          : alpha("#d32f2f", 0.2)
                      }`,
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    Reserved for MOS:
                  </Typography>
                  <Chip
                    icon={<LockIcon sx={{ fontSize: "12px !important" }} />}
                    size="small"
                    label={`${item.mos_reserved_qty || 0}`}
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.75rem",
                      height: 22,
                      bgcolor:
                        (item.mos_reserved_qty || 0) > 0
                          ? alpha("#7b1fa2", 0.12)
                          : alpha("#757575", 0.08),
                      color:
                        (item.mos_reserved_qty || 0) > 0
                          ? "#7b1fa2"
                          : "#757575",
                      border: `1px solid ${
                        (item.mos_reserved_qty || 0) > 0
                          ? alpha("#7b1fa2", 0.3)
                          : alpha("#757575", 0.2)
                      }`,
                    }}
                  />
                </Box>

                {/* Inline Quick Reserve / Release Controls */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mt: 0.5,
                  }}
                >
                  <TextField
                    size="small"
                    type="number"
                    placeholder="Reserve Qty"
                    value={
                      item.reserve_input_qty !== undefined
                        ? item.reserve_input_qty
                        : ""
                    }
                    onChange={(e) =>
                      handleItemChange(
                        index,
                        "reserve_input_qty",
                        e.target.value
                      )
                    }
                    inputProps={{ min: 0, max: item.free_stock || 0 }}
                    sx={{ flex: 1 }}
                  />
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    disabled={
                      reservingIndex === index || (item.free_stock || 0) <= 0
                    }
                    onClick={() => handleInlineReserve(index)}
                    startIcon={
                      reservingIndex === index ? (
                        <CircularProgress size={12} color="inherit" />
                      ) : (
                        <LockIcon sx={{ fontSize: 13 }} />
                      )
                    }
                    sx={{ textTransform: "none", height: 36 }}
                  >
                    Reserve
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="secondary"
                    disabled={
                      reservingIndex === index ||
                      (parseFloat(item.mos_reserved_qty) || 0) <= 0
                    }
                    onClick={() => handleInlineRelease(index)}
                    sx={{ textTransform: "none", height: 36 }}
                  >
                    Release
                  </Button>
                </Box>
              </Box>

              <TextField
                label="PO Qty (To Order)"
                type="number"
                fullWidth
                size="small"
                value={item.quantity}
                onChange={(e) => handleItemChange(index, "quantity", e.target.value)}
                error={!!itemFieldErrors[index]?.quantity}
                helperText={
                  itemFieldErrors[index]?.quantity ||
                  (parseFloat(item.quantity || 0) === 0 &&
                  (parseFloat(item.mos_reserved_qty || 0) > 0 ||
                    parseFloat(item.reserve_qty || 0) > 0)
                    ? "✓ Fully Fulfilled from Stock"
                    : "")
                }
                sx={{
                  mb: 2,
                  "& .MuiFormHelperText-root": {
                    color:
                      parseFloat(item.quantity || 0) === 0 &&
                      (parseFloat(item.mos_reserved_qty || 0) > 0 ||
                        parseFloat(item.reserve_qty || 0) > 0) &&
                      !itemFieldErrors[index]?.quantity
                        ? "success.main"
                        : undefined,
                    fontWeight: 600,
                  },
                }}
              />

              <Autocomplete
                size="small"
                options={Array.isArray(unitOptions) ? unitOptions : []}
                getOptionLabel={(option) =>
                  typeof option === "object"
                    ? option?.name || option?.unit_name || ""
                    : ""
                }
                value={
                  item.unit
                    ? unitOptions.find((u) => String(u.id) === String(item.unit)) || null
                    : null
                }
                onChange={(e, newValue) => handleInputChange(index, "unit", newValue)}
                isOptionEqualToValue={(option, value) =>
                  String(option?.id) === String(value?.id)
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="UOM"
                    size="small"
                    fullWidth
                    error={!!itemFieldErrors[index]?.unit}
                    helperText={itemFieldErrors[index]?.unit}
                    sx={{ mb: 2 }}
                  />
                )}
              />

              <TextField
                label="Unit Price"
                type="number"
                fullWidth
                size="small"
                value={item.rate}
                onChange={(e) => handleItemChange(index, "rate", e.target.value)}
                error={!!itemFieldErrors[index]?.rate}
                helperText={itemFieldErrors[index]?.rate}
                sx={{ mb: 2 }}
              />

              <TextField
                label="Amount"
                type="number"
                fullWidth
                size="small"
                value={item.amount}
                onChange={(e) => handleItemChange(index, "amount", e.target.value)}
                sx={{ mb: 2 }}
              />

              <TextField
                label="Total"
                fullWidth
                size="small"
                value={(parseFloat(item.rate || 0) * parseFloat(item.quantity || 0)).toFixed(2)}
                InputProps={{ readOnly: true }}
                sx={{
                  "& .MuiInputBase-input": {
                    backgroundColor: "#f5f5f5",
                  },
                }}
              />
            </Box>
          ))}

          <Box textAlign="center" sx={{ mb: 3 }}>
            <Button variant="outlined" onClick={addItemRow} startIcon={<AddIcon />} fullWidth>
              Add Item
            </Button>
          </Box>

          <Box
            sx={{
              p: 2,
              border: "1px solid #ccc",
              borderRadius: "8px",
              backgroundColor: "#fff",
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
              <strong>Grand Total:</strong>
              <TextField
                value={totalValue.toFixed(2)}
                size="small"
                disabled
                sx={{
                  width: "150px",
                  "& .MuiInputBase-input": {
                    fontWeight: "bold",
                    textAlign: "right",
                  },
                }}
              />
            </Box>
          </Box>
        </Box>
      ) : (
        <>
          <TableContainer sx={{ mt: 2 }}>
            <Box border="1px solid #ccc" borderRadius="8px" sx={{ mb: 3, p: 2, minWidth: 2400 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f5f5f5", fontWeight: "bold" }}>
                    <TableCell>S.No</TableCell>
                    <TableCell>Product Type</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Item</TableCell>
                    <TableCell>UOM</TableCell>
                    <TableCell align="center">MOS Req</TableCell>
                    <TableCell align="center">Free Stock</TableCell>
                    <TableCell align="center">MOS Reserved</TableCell>
                    <TableCell align="center">Quick Reserve / Release</TableCell>
                    <TableCell align="center">PO Qty</TableCell>
                    <TableCell align="right">Unit Price</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="center">Action</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {formData.items.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{index + 1}</TableCell>

                      {/* Product Type */}
                      <TableCell>
                        <Autocomplete
                          size="small"
                          options={Array.isArray(sizeOptions) ? sizeOptions : []}
                          getOptionLabel={(option) => option?.size || option?.name || ""}
                          value={
                            item.size
                              ? typeof item.size === "object"
                                ? (Array.isArray(sizeOptions)
                                    ? sizeOptions.find(
                                        (s) =>
                                          (item.size.id &&
                                            String(s.id) === String(item.size.id)) ||
                                          (s.size &&
                                            item.size.size &&
                                            String(s.size).trim().toLowerCase() ===
                                              String(item.size.size).trim().toLowerCase())
                                      )
                                    : null) || item.size
                                : (Array.isArray(sizeOptions)
                                    ? sizeOptions.find((s) => String(s.id) === String(item.size))
                                    : null) || null
                              : null
                          }
                          onChange={(e, newValue) =>
                            handleInputChange(index, "size", newValue)
                          }
                          onInputChange={(e, newInputValue, reason) => {
                            if (reason === "input") {
                              handleItemInputChange(index, item, newInputValue, "size");
                            }
                          }}
                          isOptionEqualToValue={(option, value) =>
                            String(option?.id) === String(value?.id) ||
                            (option?.size &&
                              value?.size &&
                              String(option.size).trim().toLowerCase() ===
                                String(value.size).trim().toLowerCase())
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Product Type"
                              size="small"
                              sx={{ width: "160px" }}
                              error={!!itemFieldErrors[index]?.size}
                              helperText={itemFieldErrors[index]?.size}
                            />
                          )}
                        />
                      </TableCell>

                      {/* Category */}
                      <TableCell>
                        <Autocomplete
                          options={rowCategoryOptions[index] || []}
                          getOptionLabel={(opt) => opt.name || ""}
                          isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                          value={item.category || null}
                          disabled={!item.size}
                          onChange={(e, val, reason) => {
                            if (reason === "clear" || !val) {
                              handleInputChange(index, "category", null);
                              return;
                            }
                            handleInputChange(index, "category", val);
                          }}
                          onInputChange={(e, newValue, reason) => {
                            if (reason === "input") {
                              handleItemInputChange(index, item, newValue, "category");
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Category"
                              size="small"
                              sx={{ width: "180px" }}
                              required
                              error={!!itemFieldErrors[index]?.category}
                            />
                          )}
                        />
                      </TableCell>

                      {/* Item */}
                      <TableCell>
                        <Autocomplete
                          options={Array.isArray(itemOptions[index]) ? itemOptions[index] : []}
                          getOptionLabel={(opt) => opt.name || ""}
                          isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                          value={item.item || null}
                          disabled={!item.category}
                          onChange={(e, val, reason) => {
                            if (reason === "clear" || !val) {
                              handleInputChange(index, "item", null);
                              return;
                            }
                            handleInputChange(index, "item", val);
                          }}
                          onInputChange={(e, newValue) =>
                            handleItemInputChange(index, item, newValue, "item")
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small"
                              sx={{ width: "180px" }}
                              label="Item"
                              required
                              error={!!itemFieldErrors[index]?.item}
                              helperText={itemFieldErrors[index]?.item}
                            />
                          )}
                        />
                      </TableCell>

                      {/* UOM */}
                      <TableCell>
                        <Autocomplete
                          size="small"
                          options={unitOptions || []}
                          getOptionLabel={(option) =>
                            typeof option === "object"
                              ? option?.name || option?.unit_name || ""
                              : ""
                          }
                          value={
                            item.unit
                              ? unitOptions.find((u) => String(u.id) === String(item.unit)) || null
                              : null
                          }
                          onChange={(e, newValue) =>
                            handleInputChange(index, "unit", newValue)
                          }
                          isOptionEqualToValue={(option, value) =>
                            String(option?.id) === String(value?.id)
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Unit"
                              size="small"
                              sx={{ width: "130px" }}
                              error={!!itemFieldErrors[index]?.unit}
                              helperText={itemFieldErrors[index]?.unit}
                            />
                          )}
                        />
                      </TableCell>

                      {/* MOS Required */}
                      <TableCell align="center">
                        <input
                          type="number"
                          value={item.required_qty || 0}
                          style={{
                            width: "70px",
                            padding: "8px",
                            height: "36px",
                            fontSize: "0.875rem",
                            borderRadius: "4px",
                            backgroundColor: "#f5f5f5",
                            border: "1px solid #c4c4c4",
                            textAlign: "center",
                            boxSizing: "border-box",
                          }}
                          disabled
                        />
                      </TableCell>

                      {/* Free Stock in Warehouse */}
                      <TableCell align="center">
                        <Tooltip
                          title={`Current Physical: ${
                            item.current_stock || 0
                          } | Total Reserved: ${item.reserve_stock || 0}`}
                        >
                          <Chip
                            size="small"
                            label={`${item.free_stock || 0}`}
                            sx={{
                              fontWeight: 700,
                              fontSize: "0.75rem",
                              height: 24,
                              bgcolor:
                                (item.free_stock || 0) > 0
                                  ? alpha("#2e7d32", 0.12)
                                  : alpha("#d32f2f", 0.08),
                              color:
                                (item.free_stock || 0) > 0
                                  ? "#2e7d32"
                                  : "#d32f2f",
                              border: `1px solid ${
                                (item.free_stock || 0) > 0
                                  ? alpha("#2e7d32", 0.3)
                                  : alpha("#d32f2f", 0.2)
                              }`,
                            }}
                          />
                        </Tooltip>
                      </TableCell>

                      {/* MOS Reserved */}
                      <TableCell align="center">
                        <Tooltip
                          title={`Stock allocated for this MOS: ${
                            item.mos_reserved_qty || 0
                          }`}
                        >
                          <Chip
                            icon={
                              <LockIcon sx={{ fontSize: "12px !important" }} />
                            }
                            size="small"
                            label={`${item.mos_reserved_qty || 0}`}
                            sx={{
                              fontWeight: 700,
                              fontSize: "0.75rem",
                              height: 24,
                              bgcolor:
                                (item.mos_reserved_qty || 0) > 0
                                  ? alpha("#7b1fa2", 0.12)
                                  : alpha("#757575", 0.08),
                              color:
                                (item.mos_reserved_qty || 0) > 0
                                ? "#7b1fa2"
                                : "#757575",
                              border: `1px solid ${
                                (item.mos_reserved_qty || 0) > 0
                                  ? alpha("#7b1fa2", 0.3)
                                  : alpha("#757575", 0.2)
                              }`,
                            }}
                          />
                        </Tooltip>
                      </TableCell>

                      {/* Quick Inline Reserve / Release */}
                      <TableCell align="center">
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            justifyContent: "center",
                          }}
                        >
                          <input
                            type="number"
                            placeholder="Qty"
                            value={
                              item.reserve_input_qty !== undefined
                                ? item.reserve_input_qty
                                : ""
                            }
                            onChange={(e) =>
                              handleItemChange(
                                index,
                                "reserve_input_qty",
                                e.target.value
                              )
                            }
                            style={{
                              width: "55px",
                              height: "28px",
                              textAlign: "center",
                              fontSize: "0.75rem",
                              padding: "2px 4px",
                              borderRadius: "4px",
                              border: "1px solid #c4c4c4",
                            }}
                            min="0"
                            max={item.free_stock || 0}
                          />
                          <Tooltip
                            title={`Reserve free warehouse stock for this MOS (Available: ${
                              item.free_stock || 0
                            })`}
                          >
                            <span>
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                disabled={
                                  reservingIndex === index ||
                                  (item.free_stock || 0) <= 0
                                }
                                onClick={() => handleInlineReserve(index)}
                                sx={{
                                  minWidth: "auto",
                                  px: 1,
                                  py: 0.2,
                                  height: "28px",
                                  fontSize: "0.7rem",
                                  textTransform: "none",
                                  boxShadow: "none",
                                }}
                                startIcon={
                                  reservingIndex === index ? (
                                    <CircularProgress
                                      size={12}
                                      color="inherit"
                                    />
                                  ) : (
                                    <LockIcon sx={{ fontSize: 13 }} />
                                  )
                                }
                              >
                                Reserve
                              </Button>
                            </span>
                          </Tooltip>
                          <Tooltip
                            title={
                              (parseFloat(item.mos_reserved_qty) || 0) > 0
                                ? `Release reserved stock back to free pool (Reserved: ${
                                    item.mos_reserved_qty || 0
                                  })`
                                : "No reserved stock for this item"
                            }
                          >
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                color="secondary"
                                disabled={
                                  reservingIndex === index ||
                                  (parseFloat(item.mos_reserved_qty) || 0) <= 0
                                }
                                onClick={() => handleInlineRelease(index)}
                                sx={{
                                  minWidth: "auto",
                                  px: 0.8,
                                  py: 0.2,
                                  height: "28px",
                                  fontSize: "0.7rem",
                                  textTransform: "none",
                                }}
                              >
                                Release
                              </Button>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>

                      {/* PO Qty (Editable) */}
                      <TableCell align="center">
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, "quantity", e.target.value)}
                          style={{
                            width: "100px",
                            height: "36px",
                            padding: "8px",
                            textAlign: "center",
                            fontSize: "0.875rem",
                            borderRadius: "4px",
                            backgroundColor:
                              parseFloat(item.quantity || 0) === 0 &&
                              (parseFloat(item.mos_reserved_qty || 0) > 0 ||
                                parseFloat(item.reserve_qty || 0) > 0)
                                ? "#e8f5e9"
                                : "#fcfcfc",
                            border: itemFieldErrors[index]?.quantity
                              ? "1px solid red"
                              : "1px solid #c4c4c4",
                            boxSizing: "border-box",
                          }}
                        />
                        {parseFloat(item.quantity || 0) === 0 &&
                          (parseFloat(item.mos_reserved_qty || 0) > 0 ||
                            parseFloat(item.reserve_qty || 0) > 0) && (
                            <Chip
                              size="small"
                              label="Stock Fulfilled"
                              color="success"
                              sx={{
                                height: 18,
                                fontSize: "0.65rem",
                                fontWeight: 700,
                                mt: 0.5,
                              }}
                            />
                          )}
                        {itemFieldErrors[index]?.quantity && (
                          <div style={{ color: "red", fontSize: "0.8rem", marginTop: "4px" }}>
                            {itemFieldErrors[index]?.quantity}
                          </div>
                        )}
                      </TableCell>

                      <TableCell align="right">
                        <input
                          type="number"
                          value={item.rate}
                          onChange={(e) => handleItemChange(index, "rate", e.target.value)}
                          style={{
                            width: "110px",
                            padding: "8px",
                            height: "36px",
                            fontSize: "0.875rem",
                            borderRadius: "4px",
                            backgroundColor: "#fcfcfc",
                            textAlign: "right",
                            border: itemFieldErrors[index]?.rate
                              ? "1px solid red"
                              : "1px solid #c4c4c4",
                            boxSizing: "border-box",
                          }}
                        />
                        {itemFieldErrors[index]?.rate && (
                          <div style={{ color: "red", fontSize: "0.8rem", marginTop: "4px" }}>
                            {itemFieldErrors[index]?.rate}
                          </div>
                        )}
                      </TableCell>

                      <TableCell align="right">
                        <input
                          type="number"
                          value={item.amount}
                          onChange={(e) => handleItemChange(index, "amount", e.target.value)}
                          style={{
                            width: "120px",
                            padding: "8px",
                            height: "36px",
                            fontSize: "0.875rem",
                            borderRadius: "4px",
                            backgroundColor: "#fcfcfc",
                            textAlign: "right",
                            border: "1px solid #c4c4c4",
                            boxSizing: "border-box",
                          }}
                        />
                      </TableCell>

                      <TableCell align="right">
                        <input
                          type="text"
                          readOnly
                          value={(parseFloat(item.rate || 0) * parseFloat(item.quantity || 0)).toFixed(2)}
                          style={{
                            width: "120px",
                            height: "36px",
                            padding: "8px",
                            fontSize: "0.875rem",
                            borderRadius: "4px",
                            backgroundColor: "#f5f5f5",
                            textAlign: "right",
                            border: "1px solid #c4c4c4",
                            boxSizing: "border-box",
                          }}
                        />
                      </TableCell>

                      <TableCell>
                        <input
                          type="text"
                          value={item.description}
                          onChange={(e) => handleItemChange(index, "description", e.target.value)}
                          style={{
                            width: "180px",
                            height: "36px",
                            padding: "8px",
                            fontSize: "0.875rem",
                            borderRadius: "4px",
                            backgroundColor: "#fcfcfc",
                            border: "1px solid #c4c4c4",
                            boxSizing: "border-box",
                          }}
                        />
                      </TableCell>

                      <TableCell align="center">
                        <IconButton onClick={() => deleteItemRow(index)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </TableContainer>

          <TableContainer>
            <Box sx={{ mb: 3, width: "100%" }}>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={15} align="center">
                      <Button variant="outlined" onClick={addItemRow} startIcon={<AddIcon />}>
                        Add Item
                      </Button>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell colSpan={15}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          alignItems: "center",
                          gap: 2,
                        }}
                      >
                        <strong>Grand Total</strong>
                        <TextField
                          value={totalValue.toFixed(2)}
                          size="small"
                          sx={{ width: "150px" }}
                          disabled
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </TableContainer>
        </>
      )}

      {/* Terms & Conditions */}
      <Box border="1px solid #ccc" borderRadius="8px" sx={{ mb: 3, p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Terms & Conditions
        </Typography>

        {terms.map((term, index) => (
          <Grid container spacing={1} key={index} alignItems="center" sx={{ mb: 1 }}>
            <Grid item xs={5}>
              <TextField
                fullWidth
                placeholder="Term (e.g. Delivery Period)"
                value={term.key}
                onChange={(e) => handleTermChange(index, "key", e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                placeholder="Value (e.g. Within 15 days)"
                value={term.value}
                onChange={(e) => handleTermChange(index, "value", e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={1}>
              <IconButton onClick={() => removeTerm(index)} color="error">
                <DeleteIcon />
              </IconButton>
            </Grid>
          </Grid>
        ))}

        <Button
          variant="outlined"
          sx={{ mt: 1 }}
          onClick={addTerm}
        >
          + Add Term
        </Button>
      </Box>
    </Box>
  );
}